import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Roles } from '@sc/types';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { SupportCenterService } from '../../services/support-center/support-center.service';
import { ThemeService } from '../../services/theme/theme.service';
import { SettingsService } from '../../services/settings/settings.service';
import { QuoteService } from '../../services/quote/quote.service';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { ProTip, Quote } from '@sc/types';
import { SupportArticles, SupportTopics } from '@sc/types';

@Component({
  selector: 'sc-request-permission',
  templateUrl: 'request-permission.component.html',
  styleUrls: ['request-permission.component.scss'],
})
export class RequestPermissionComponent implements OnInit, OnDestroy {
  @Output() dismiss = new EventEmitter<{ hasPermission: boolean[] }>();

  theme$ = this.themeService.theme$;

  fullHD: ConstrainBoolean;
  dismissed = false;
  headShake = false;
  quotes: Array<Quote>;
  currentQuote: number = null;
  quoteInterval: NodeJS.Timer;
  fadeOut = false;
  proTip: ProTip;
  proTipInterval: NodeJS.Timer;
  proTipIndex = 0;
  Roles = Roles;
  SupportArticles = SupportArticles;

  tips: Array<ProTip> = [
    {
      title: 'Keep Focus on this Browser Tab',
      description: 'Browsers throttle background tabs and that can impact your quality and connection.',
      icon: 'focus-tab',
      topic: 'netConnSecurity',
    },
    {
      title: 'Your Internet Matters',
      description: 'Use a hardwired connection or strong WiFi and reset if needed.',
      icon: 'wifi-very-strong',
      topic: 'netConnSecurity',
    },
    {
      title: 'Setup Your Microphone Now',
      description: 'Plug it in, power it on, and select it in your Sound Settings.',
      icon: 'microphone',
      topic: 'selectEquipment',
    },
    {
      title: 'Wear Headphones to Avoid Feedback',
      description: 'If you hear feedback, ask the other participants to turn it down or mute.',
      icon: 'headphones',
      topic: 'wearHeadphones',
    },
    {
      title: 'Setup Your Microphone Now',
      description: 'Plug it in, power it on, and select it in your Sound Settings.',
      icon: 'microphone',
      topic: 'selectEquipment',
    },
  ];

  subs: Array<Subscription> = [];

  constructor(
    private equipmentService: EquipmentService,
    private quoteService: QuoteService,
    private settingsService: SettingsService,
    private supportCenterService: SupportCenterService,
    private themeService: ThemeService,
    private toastrService: ToastrService,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit() {
    this.equipmentService.init();
    this.setupFullHD();
    this.setupProTip();
    this.setupQuotes();
    this.setupPermissions();
  }

  async setupPermissions() {
    const sub = this.equipmentService.hasPermission$.subscribe(async (hasPermission) => {
      if (!hasPermission[0]) {
        this.toastrService.error('You need to allow at least microphone access to continue.', 'Permissions Required', {
          tapToDismiss: false,
          toastComponent: GeneralToastComponent,
        });
        await this.analyticsService.track('opened request permission warning');
        return;
      }
      this.dismiss.emit({ hasPermission });
      this.dismissed = true;
    });

    this.subs.push(sub);
  }

  async getStarted() {
    this.headShake = false; // reset

    setTimeout(() => {
      this.headShake = true;
    }, 500);
  }

  async learnMore(topic: SupportTopics) {
    this.supportCenterService.topic(topic);
  }

  async openSupportCenter() {
    this.supportCenterService.topic('browserPermissions');
  }

  setupFullHD() {
    const sub = this.settingsService.studioShowSettings$.subscribe((settings) => {
      this.fullHD = settings.fullHD;
    });

    this.subs.push(sub);
  }

  setupQuotes() {
    const sub = this.quoteService.getQuotes().subscribe((quotes: Quote[]) => {
      if (!quotes) {
        return;
      }

      if (this.currentQuote === null) {
        this.quotes = Object.values(quotes);
        this.currentQuote = Math.floor(Math.random() * this.quotes.length);
        this.quoteInterval = setInterval(() => {
          this.fadeOut = true;
          setTimeout(() => {
            if (this.currentQuote === this.quotes.length - 1) {
              this.currentQuote = 0;
            } else {
              this.currentQuote++;
            }
            this.fadeOut = false;
          }, 1000);
        }, 10 * 1000);
      }
    });

    this.subs.push(sub);
  }

  setupProTip() {
    this.proTip = this.tips[this.proTipIndex];

    this.proTipInterval = setInterval(() => {
      this.proTipIndex = this.proTipIndex + 1;

      if (this.proTipIndex >= this.tips.length) {
        this.proTipIndex = 0;
      }

      this.proTip = this.tips[this.proTipIndex];
    }, 8 * 1000);
  }

  ngOnDestroy() {
    if (this.proTipInterval) {
      clearInterval(this.proTipInterval);
    }

    if (this.quoteInterval) {
      clearInterval(this.quoteInterval);
    }

    this.subs.map((sub: Subscription) => {
      sub.unsubscribe();
    });
  }
}
